<template>
  <v-container>
    <v-row justify="center">
      <v-col cols="12" md="8" lg="6">

        <v-card elevation="4">
          <v-card-title class="text-h5 bg-primary text-white">
            Gerenciar Instância WhatsApp
          </v-card-title>
          
          <v-card-text class="mt-4">
            <v-text-field
              v-model="tokenInput"
              label="Nome da Sessão (ex: TokenGPSOFT)"
              hint="Digite o nome da instância para conectar ou verificar status"
              persistent-hint
              variant="outlined"
              @keyup.enter="handleAction"
              :disabled="isLoading"
              prepend-inner-icon="mdi-whatsapp"
            ></v-text-field>

            <v-row class="mt-2">
              <v-col cols="6">
                <v-btn
                  @click="handleAction"
                  color="success"
                  block
                  size="large"
                  :loading="isLoading"
                  :disabled="!tokenInput"
                >
                  Conectar / Status
                </v-btn>
              </v-col>
              <v-col cols="6">
                <v-btn
                  @click="reconnectInstance"
                  color="error"
                  variant="outlined"
                  block
                  size="large"
                  :disabled="!tokenInput || isLoading"
                >
                  Limpar e Reiniciar
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- ÁREA DE RESULTADO (QR CODE OU STATUS) -->
        <v-card class="mt-5 text-center pa-10" v-if="showResultArea" min-height="350">
          
          <!-- Loading State -->
          <div v-if="isLoading">
            <v-progress-circular indeterminate color="primary" size="80" width="8"></v-progress-circular>
            <p class="mt-5 text-h6">{{ statusMessage }}</p>
            <p class="text-caption">Isso pode levar alguns segundos enquanto limpamos o cache do servidor.</p>
          </div>
          
          <!-- QR Code State -->
          <div v-else-if="qrCodeBase64">
            <h3 class="mb-4 text-h5">Leia o QR Code</h3>
            <v-img :src="qrCodeBase64" width="300" class="mx-auto elevation-3 rounded-lg"></v-img>
            <v-alert type="info" class="mt-5" variant="tonal" density="compact">
              O status será atualizado automaticamente após a leitura.
            </v-alert>
          </div>
          
          <!-- Result State (Connected / Error) -->
          <div v-else>
            <v-icon 
              v-if="isConnected" 
              color="success" 
              size="100" 
              icon="mdi-check-circle-outline"
              class="mb-4"
            ></v-icon>
            <v-alert :type="alertType" variant="tonal" prominent border="top">
              <h3 class="text-h6">Instância: {{ tokenInput }}</h3>
              <div class="text-body-1 mt-2">{{ statusMessage }}</div>
            </v-alert>
          </div>

        </v-card>

      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from 'axios';
import { io } from 'socket.io-client';

export default {
  name: 'ConectarInstancia',
  data() {
    return {
      tokenInput: '',
      qrCodeBase64: null,
      statusMessage: '',
      isLoading: false,
      showResultArea: false,
      socket: null,
      currentStatus: '',
      // URL de Produção do seu Backend
      BASE_URL: 'https://api.lubzap.lubconsulta.com.br'
    };
  },
  computed: {
    isConnected() {
      return ['CONNECTED', 'inChat'].includes(this.currentStatus);
    },
    alertType() {
      if (this.isConnected) return 'success';
      if (this.currentStatus === 'ERROR' || this.statusMessage.includes('Erro')) return 'error';
      return 'info';
    },
  },
  methods: {
    // 1. Decide se apenas checa o status ou inicia uma nova conexão
    async handleAction() {
      if (!this.tokenInput) return;
      this.isLoading = true;
      this.showResultArea = true;
      this.qrCodeBase64 = null;
      this.statusMessage = 'Consultando servidor...';

      try {
        const check = await axios.get(`${this.BASE_URL}/status/${this.tokenInput}`);
        this.currentStatus = check.data.status;

        if (this.currentStatus === 'connected') {
          this.isLoading = false;
          this.statusMessage = 'Esta instância já está conectada e ativa!';
        } else {
          // Se não estiver conectada, dispara o processo de start
          this.startConnection();
        }
      } catch (error) {
        // Se a instância não existir no Map, o /status dá 404, então iniciamos
        this.startConnection();
      }
    },

    // 2. Chama o endpoint /start que agora limpa a pasta automaticamente
    async startConnection() {
      this.statusMessage = `Preparando servidor e limpando cache para "${this.tokenInput}"...`;
      this.qrCodeBase64 = null;
      this.isLoading = true;

      this.setupWebSocketListeners();

      try {
        const response = await axios.post(`${this.BASE_URL}/start/${this.tokenInput}`);
        this.statusMessage = response.data.message;
        // O restante acontece via WebSocket (qrcode_update ou status_update_session)
      } catch (error) {
        this.isLoading = false;
        this.currentStatus = 'ERROR';
        this.statusMessage = error.response?.data?.message || 'Erro ao contatar o backend.';
      }
    },

    // 3. Força o processo de limpeza (rm -rf token no backend)
    async reconnectInstance() {
      if (!confirm(`Deseja forçar a desconexão e limpeza da instância ${this.tokenInput}?`)) return;
      
      this.isLoading = true;
      this.showResultArea = true;
      this.statusMessage = 'Limpando rastros no servidor...';
      
      try {
        await axios.post(`${this.BASE_URL}/reconnect/${this.tokenInput}`);
        // Após limpar, aguarda 2 segundos e inicia do zero
        setTimeout(() => this.startConnection(), 2000);
      } catch (error) {
        this.startConnection();
      }
    },
    
    setupWebSocketListeners() {
      // Configuração para nuvem (Elastic Beanstalk / S3)
      this.socket = io(this.BASE_URL, {
        transports: ['websocket'],
        upgrade: false,
        query: {
          token: this.tokenInput
        }
      });

      this.socket.on('qrcode_update', (data) => {
        if (data.token === this.tokenInput) {
          this.isLoading = false;
          this.qrCodeBase64 = data.qrcode;
          this.statusMessage = 'QR Code gerado com sucesso.';
        }
      });

      this.socket.on('status_update_session', (data) => {
        if (data.token === this.tokenInput) {
            this.currentStatus = data.status;
            
            if(this.isConnected) {
                this.isLoading = false;
                this.qrCodeBase64 = null;
                this.statusMessage = 'WhatsApp Conectado com sucesso!';
            } else if (data.status === 'STARTING') {
                this.isLoading = true;
                this.statusMessage = 'Iniciando navegador no servidor...';
            } else if (data.status === 'CLOSED') {
                this.isLoading = false;
                this.statusMessage = 'A conexão foi fechada.';
            }
        }
      });
    }
  },
  created() {
    //this.setupWebSocketListeners();
  },
  beforeUnmount() {
    if (this.socket) {
      this.socket.disconnect();
    }
  },
};
</script>

<style scoped>
.bg-primary {
  background-color: #1976D2 !important;
}
.mx-auto {
  margin-left: auto;
  margin-right: auto;
}
.rounded-lg {
  border-radius: 12px !important;
}
</style>